import React, { useMemo, useState } from "react";
import { Seo, orgJsonLd, softwareJsonLd } from "../components/Seo";
import { useNavigate } from "react-router-dom";
import { TotalReachLogo } from "../components/Logo";
import { PublicNav } from "../components/PublicNav";
import { StickyBookDemo } from "../components/StickyBookDemo";
import { DemoVideo } from "../components/DemoVideo";
import { useExperiment, trackExperimentClick } from "../lib/experiments";
import {
  Tv, Monitor, Globe, Radio, Smartphone, MapPin, Share2, Search, RefreshCcw, Megaphone,
  ArrowRight, CheckCircle2, XCircle, Mail, Phone, LineChart, Users2, Sparkles, Target, LayoutDashboard,
  Bot, Wand2, Gauge, ShieldCheck, HandCoins, FileUp, Zap, Palette, Landmark, Clock,
  Presentation, Plane, Bus, Building2, Building, Dumbbell, Trophy, Ticket, ShoppingBag, ShoppingCart, Fuel,
  BarChart3, Newspaper, Lightbulb, Scale, MessageSquare, Radar, DollarSign, AlertTriangle, Briefcase,
  Bell, QrCode, CalendarDays, FileText, UserPlus, Layers, HeartHandshake, Users, PieChart, BrainCircuit,
  X,
} from "lucide-react";

// Full module catalog — single source of truth for both the marketing tile grid
// and the client-side search. `k` (keywords) drives partial matches so typing
// "SMS" hits Text-to-Give, "PAC" hits both PAC Alerts and PAC Discovery, etc.
const MODULE_CATALOG = [
  { icon: BrainCircuit, t: "AI Chief of Staff", c: "cyan", g: "AI", k: "ai chief staff ask totalreach assistant copilot brief pdf" },
  { icon: Bot, t: "AI Campaign Agent", c: "cyan", g: "AI", k: "ai campaign agent plan audience budget" },
  { icon: Palette, t: "AI Creative Studio", c: "cyan", g: "AI", k: "ai creative studio ad copy display ctv script video" },
  { icon: Wand2, t: "Weekly Refreshes", c: "cyan", g: "AI", k: "weekly creative refresh brand library auto push" },
  { icon: Users2, t: "AI Voter Audiences", c: "cyan", g: "AI", k: "ai voter audiences segments persuadable turnout" },
  { icon: Gauge, t: "AI Optimization", c: "cyan", g: "AI", k: "ai optimization pacing frequency reach fatigue" },
  { icon: Radar, t: "Meta Ad Library", c: "fuchsia", g: "Opposition", k: "meta facebook opposition ads library count playwright" },
  { icon: Radar, t: "Google Transparency", c: "fuchsia", g: "Opposition", k: "google transparency ads opposition deep link" },
  { icon: DollarSign, t: "AdLibrary.com", c: "fuchsia", g: "Opposition", k: "adlibrary paid opposition per ad detail spend estimate creative" },
  { icon: AlertTriangle, t: "PAC Spend Alerts", c: "fuchsia", g: "Opposition", k: "pac spend threshold alerts schedule e independent expenditures notify" },
  { icon: Landmark, t: "PAC Discovery", c: "fuchsia", g: "Opposition", k: "pac discovery fec suggest ai schedule e enrichment" },
  { icon: BarChart3, t: "Polling Engine", c: "fuchsia", g: "Opposition", k: "polling averages momentum race snapshot" },
  { icon: Newspaper, t: "Issue Intelligence", c: "fuchsia", g: "Opposition", k: "news headlines issue intelligence digest opposition" },
  { icon: HandCoins, t: "Stripe Donations", c: "emerald", g: "Fundraising", k: "stripe donations recurring refund chargeback donor receipt" },
  { icon: MessageSquare, t: "Text-to-Give SMS", c: "emerald", g: "Fundraising", k: "sms text to give twilio keyword donate donation" },
  { icon: Bell, t: "Donor Auto-Thanks", c: "emerald", g: "Fundraising", k: "donor auto thanks email drip receipt recurring" },
  { icon: PieChart, t: "Source Economics", c: "emerald", g: "Fundraising", k: "source economics attribution channel roas fundraising per source" },
  { icon: Tv, t: "CTV Trafficking", c: "cyan", g: "Advertising", k: "ctv connected tv trafficking video campaign" },
  { icon: Briefcase, t: "Agency Portfolio", c: "cyan", g: "Agency", k: "agency portfolio multi entity consultant clients" },
  { icon: Users, t: "Team & Access", c: "cyan", g: "Agency", k: "team access invite roles cross entity bulk" },
  { icon: Layers, t: "Brand Library", c: "cyan", g: "Agency", k: "brand library logo tagline slogans key issues" },
  { icon: FileText, t: "Weekly Brief PDF", c: "cyan", g: "Ops", k: "weekly executive brief pdf export report" },
  { icon: HeartHandshake, t: "Volunteer Portal", c: "amber", g: "Volunteers", k: "volunteer portal shift signup public sighting" },
  { icon: Trophy, t: "Volunteer Leaderboard", c: "amber", g: "Volunteers", k: "volunteer leaderboard points tier ranking gamified" },
  { icon: QrCode, t: "QR Attribution", c: "amber", g: "Volunteers", k: "qr codes attribution tracking mailer podium" },
  { icon: CalendarDays, t: "Events + RSVP", c: "amber", g: "Volunteers", k: "events rsvp checkin public microsite shifts" },
  { icon: ShieldCheck, t: "Approval Chains", c: "cyan", g: "Ops", k: "approval chains budget threshold escalate compliance" },
  { icon: Scale, t: "Compliance Audit", c: "cyan", g: "Ops", k: "compliance audit fec disclosure disclaimer" },
  { icon: CheckCircle2, t: "Launch Checklist", c: "cyan", g: "Ops", k: "launch checklist onboarding 8 step verify brand microsite team" },
  { icon: Globe, t: "Public Microsites", c: "cyan", g: "Ops", k: "public microsites landing pages donation rsvp" },
];

const CHANNELS = [
  { icon: Globe, label: "Premium Websites" }, { icon: Tv, label: "Connected TV" },
  { icon: Monitor, label: "Display" }, { icon: Megaphone, label: "Video" },
  { icon: LayoutDashboard, label: "Native" }, { icon: Radio, label: "Audio & Podcasts" },
  { icon: Smartphone, label: "Mobile & In-Game" }, { icon: MapPin, label: "Digital Out-of-Home" },
  { icon: Share2, label: "Paid Social" }, { icon: Search, label: "Google Campaigns" },
  { icon: RefreshCcw, label: "Retargeting" }, { icon: Share2, label: "Social Publishing" },
];

const AI_FEATURES = [
  { icon: BrainCircuit, t: "Ask TotalReach — AI Chief of Staff", d: "A floating AI agent inside every screen that reads your live race, budget, approvals and calendar — and can create events, approve pending items, send the daily brief or export the whole chat as a briefing PDF." },
  { icon: Bot, t: "AI Campaign Agent", d: "Describe your race in plain English — the AI builds the audience, geography, channel mix, budget plan and forecast, then launches with one human approval." },
  { icon: Palette, t: "No Creatives? No Problem.", d: "Our AI Creative Studio writes display ads, 15/30-second CTV and radio scripts, generates video with lifelike presenters and voiceover — compliant disclaimers included." },
  { icon: Users2, t: "AI Voter Audiences", d: "Ask for 'persuadable suburban parents in my district' and the AI interprets your intent, then recommends real, activatable voter segments with the strategy behind each pick." },
  { icon: Gauge, t: "AI Optimization Agent", d: "After launch, the AI watches pacing, frequency and reach 24/7 — flagging creative fatigue and budget drift, and answering performance questions in plain language." },
  { icon: HandCoins, t: "AI Fundraising Builder", d: "One conversation creates a donation landing page, a public microsite, a QR code for mailers and podium signs — and the ad campaign that drives to it." },
  { icon: Wand2, t: "Weekly Creative Refreshes", d: "Every week the AI Ad Studio drafts fresh creatives from your Brand Library — approve one and it auto-pushes to the ad platform in seconds." },
];

const OPPOSITION_INTEL = [
  { icon: Radar, t: "Meta Ad Library — Live Counts", d: "Headless Chromium renders Meta's political ad library and pulls real active-ad counts for your candidate, every opponent and every linked PAC — no Meta developer token required." },
  { icon: Radar, t: "Google Ads Transparency", d: "One-click deep-links into Google's political-ads viewer for every subject in your race — the second free source alongside Meta." },
  { icon: DollarSign, t: "AdLibrary.com Detail Feed", d: "Paid per-ad detail from AdLibrary.com — creative previews, spend estimates and channel mix cached and refreshed daily." },
  { icon: AlertTriangle, t: "PAC Spend Threshold Alerts", d: "Watch FEC Schedule E daily. When a linked PAC crosses your $ threshold in a rolling window (default $50k / 7 days), you get an urgent notification and it drops into the daily brief." },
  { icon: Landmark, t: "PAC Discovery — FEC + AI", d: "Auto-suggest PACs likely to hit your race using FEC Schedule E rows (with actual $ spent, support/oppose flag) plus Claude enrichment for state-specific committees." },
  { icon: UserPlus, t: "Opponent FEC Auto-Complete", d: "Type an opponent's name and instantly link their FEC candidate ID — PAC alerts expand from your candidate to the whole race in one click." },
];

const AGENCY_FEATURES = [
  { icon: Briefcase, t: "Agency Portfolio", d: "One consolidated dashboard across every client campaign you manage — totals, upcoming elections, pending approvals, AI cost per month." },
  { icon: Users, t: "Cross-Entity Team & Access", d: "Bulk-invite a consultant to 12 campaigns with per-entity roles in one email. Team matrix shows exactly who has access to what." },
  { icon: Layers, t: "Centralized Brand Library", d: "One tagline, slogan set, key issues, logo pack — the AI Chief of Staff and Ad Studio Refresh always sound like you." },
  { icon: FileText, t: "Weekly Executive Briefs (PDF)", d: "Auto-generated weekly performance briefs — spend pacing, race intelligence, fundraising totals, top approvals — as a branded PDF you can email to the candidate." },
  { icon: PieChart, t: "Budget Control Center", d: "Cross-campaign burn rate, forecast to election day, and approval-chain thresholds that automatically escalate when a change breaches your pre-set limits." },
];

const FUNDRAISING_FEATURES = [
  { icon: HandCoins, t: "Stripe Donations + Recurring Gifts", d: "One-click checkout, monthly recurring donations, receipt emails, refunds and chargeback management — no separate donor CRM." },
  { icon: MessageSquare, t: "Text-to-Give SMS", d: "Publish keywords like GIVE — texters get a personalized donation link back in seconds. Twilio-powered with a built-in dry-run pipe tester." },
  { icon: Bell, t: "Donor Auto-Thanks", d: "Every gift triggers a personalized thank-you email + recurring-donor drip series. Turn one gift into repeat givers." },
  { icon: PieChart, t: "Per-Channel Source Economics", d: "See which ad channel, microsite, QR code or SMS keyword actually raised each dollar. ROAS by source, cycle-over-cycle." },
];

const VOLUNTEER_FEATURES = [
  { icon: HeartHandshake, t: "Volunteer Portal + Shift Scheduler", d: "Public shift signup at /volunteer/:token — supporters claim shifts, RSVP to events and log opposition ad sightings from their phones." },
  { icon: Trophy, t: "Volunteer Leaderboard", d: "Points auto-award from shifts, RSVPs, check-ins and ad sightings — 5-tier ranks (Newcomer → Ambassador). Turns your volunteer base into a retention loop." },
  { icon: QrCode, t: "Trackable QR Codes", d: "Mailer, yard sign and podium QR codes generate unique attribution IDs — every scan reports back into fundraising source economics." },
  { icon: CalendarDays, t: "Events with Public RSVP", d: "Publish rallies, town halls or fundraisers with a public /m/ URL. RSVP + shift signup + check-in tracking all built in." },
];

const CLAIM_ONBOARDING = [
  { icon: CheckCircle2, t: "8-Step Launch Checklist", d: "Auto-progressing setup checklist visible on every new candidate's dashboard — verify entity → brand → fundraiser → microsite → team → event → first ad." },
  { icon: Sparkles, t: "Claim Your Race in 60 Seconds", d: "Type your name, we match your FEC candidate ID, and provision your entity, microsite, fundraising campaign and brand library — all before you sit down at your desk." },
];

const POLITICAL_FIRST = [
  { icon: Zap, t: "Same-Week Launch", d: "Go from sign-up to live ads in days, not weeks — built for the pace and pressure of an election cycle." },
  { icon: FileUp, t: "Voter File Onboarding in Hours", d: "Upload your voter or supporter list — identifiers are hashed and privacy-protected before activation, and your list becomes a targetable audience in hours." },
  { icon: Target, t: "Precision District Targeting", d: "Layer state, district, DMA, ZIP and custom map shapes with modeled partisanship, turnout propensity and issue audiences." },
  { icon: ShieldCheck, t: "Compliance Built In", d: "'Paid for by' disclaimers applied automatically from your committee profile, an audit vault for every action, and political-use-permitted data only." },
  { icon: Landmark, t: "Fundraising in the Same Platform", d: "Donation pages, microsites, recurring gifts, donor CRM and FEC-aware receipts — connected to your ads so you see which dollars came from which impression." },
  { icon: Clock, t: "Measurement Beyond Clicks", d: "Households reached, completion rates, frequency, spend pacing and ad-attributed donations — one unified report across every channel." },
];

const ENVIRONMENTS = [
  { title: "Connected TV & Streaming", note: "Representative premium streaming environments",
    items: [
      { name: "Disney+", domain: "disneyplus.com" }, { name: "Netflix", domain: "netflix.com" },
      { name: "Prime Video", domain: "primevideo.com" }, { name: "Hulu", domain: "hulu.com" },
      { name: "Peacock", domain: "peacocktv.com" }, { name: "Max", domain: "max.com" },
      { name: "Pluto TV", domain: "pluto.tv" }, { name: "Tubi", domain: "tubitv.com" },
      { name: "Roku", domain: "roku.com" }, { name: "Fire TV", domain: "amazon.com" },
      { name: "Samsung TV Plus", domain: "samsung.com" }, { name: "LG Channels", domain: "lg.com" },
      { name: "NBCUniversal", domain: "nbcuniversal.com" },
    ] },
  { title: "Premium Websites & Trusted Publishers", note: "Representative premium publisher environments",
    items: [
      { name: "The New York Times", domain: "nytimes.com" }, { name: "Bloomberg", domain: "bloomberg.com" },
      { name: "Forbes", domain: "forbes.com" }, { name: "The Guardian", domain: "theguardian.com" },
      { name: "Realtor.com", domain: "realtor.com" }, { name: "AARP", domain: "aarp.org" },
      { name: "Experian", domain: "experian.com" }, { name: "Kantar", domain: "kantar.com" },
    ] },
  { title: "Social & Search Activation", note: "Integrated social and search platforms",
    items: [
      { name: "Facebook", domain: "facebook.com" }, { name: "Instagram", domain: "instagram.com" },
      { name: "Google", domain: "google.com" }, { name: "YouTube", domain: "youtube.com" },
      { name: "TikTok", domain: "tiktok.com" }, { name: "LinkedIn", domain: "linkedin.com" },
      { name: "X", domain: "x.com" },
    ] },
  { title: "Digital Out-of-Home", note: "Digital screen placements in public environments",
    items: [
      { name: "Billboards", icon: Presentation }, { name: "Airports", icon: Plane },
      { name: "Transit Hubs", icon: Bus }, { name: "Office Buildings", icon: Building2 },
      { name: "Elevator Networks", icon: Building }, { name: "Gyms", icon: Dumbbell },
      { name: "Stadiums", icon: Trophy }, { name: "Live Event Venues", icon: Ticket },
      { name: "Malls", icon: ShoppingBag }, { name: "Grocery Stores", icon: ShoppingCart },
      { name: "Gas Stations", icon: Fuel },
    ] },
];

const JOURNEY = [
  { n: "01", t: "Awareness", d: "CTV, DOOH, Video" },
  { n: "02", t: "Consideration", d: "Display, Native, Social" },
  { n: "03", t: "Reinforcement", d: "Retargeting, Mobile" },
  { n: "04", t: "Intent", d: "Google Campaigns" },
  { n: "05", t: "Mobilize & Fund", d: "GOTV + AI Fundraising" },
];

const OS_LOOP = [
  { n: "01", t: "Know the Race", d: "Polling averages, public issue intelligence, geography and permitted political data — one live picture of where you actually stand." },
  { n: "02", t: "Build the Strategy", d: "AI turns that intelligence into budget, media-mix and geography scenarios you can compare, edit and approve." },
  { n: "03", t: "Reach the Voters", d: "Execute across CTV, digital, email, direct mail and fundraising — from the same platform that built the plan." },
  { n: "04", t: "Measure Honestly", d: "Before-vs-after dashboards keep public indicators and campaign delivery separate — evidence, never inflated claims." },
];

const INTEL_FEATURES = [
  { icon: BarChart3, t: "Polling Engine", d: "Log polls manually or upload CSVs — TotalReach computes weighted polling averages, trendlines and momentum for your race." },
  { icon: Newspaper, t: "Public Issue Intelligence", d: "Track the issues moving your electorate — salience and sentiment over time, tied directly into your strategy." },
  { icon: Lightbulb, t: "AI Strategy Scenarios", d: "Three data-grounded scenarios — budget, media mix and geography — generated from your race intelligence in seconds." },
  { icon: Scale, t: "Before vs. After Measurement", d: "Public indicators on one side, campaign delivery on the other. We never claim ads moved a poll — and neither should anyone." },
];

const DIFF_COLUMNS = [
  {
    title: "Buying ads directly on Google & social",
    tone: "bad",
    items: ["One walled garden at a time — separate logins, budgets and reports", "No CTV, streaming audio, premium publishers or DOOH",
      "Generic audiences — no voter files, no turnout models", "Compliance and disclaimers are your problem",
      "No creative help — bring your own ads", "Fundraising lives in yet another tool"],
  },
  {
    title: "Big programmatic ad platforms",
    tone: "mid",
    items: ["Powerful, but built for brands — politics is an add-on", "Steep learning curve: bids, deals, pixels, insertion orders",
      "Minimum spends and managed-service wait times", "Creative studio and strategy cost extra",
      "No fundraising, donor CRM or candidate intelligence", "You still need an agency to run it"],
  },
  {
    title: "TotalReach",
    tone: "good",
    items: ["The only platform designed for politics from the ground up", "Every channel — CTV to DOOH to social to search — in one login",
      "AI builds the plan, the audience, the creative and the optimization", "Voter files, turnout models and issue audiences ready to activate",
      "Disclaimers, audits and political compliance automated", "Ads + fundraising + donor CRM + race intelligence, connected"],
  },
];

const STATS = [
  { v: "4.2M", l: "Impressions", s: "+18%" },
  { v: "1.8M", l: "Unique Reach", s: "+12%" },
  { v: "94%", l: "Spend Pacing", s: "On Track" },
  { v: "87%", l: "Completion Rate", s: "+5%" },
];

const TILE_COLORS = {
  cyan: "border-cyan-400/20 text-cyan-300 hover:border-cyan-400/50 hover:bg-cyan-400/[0.04]",
  fuchsia: "border-fuchsia-400/20 text-fuchsia-300 hover:border-fuchsia-400/50 hover:bg-fuchsia-400/[0.04]",
  emerald: "border-emerald-400/20 text-emerald-300 hover:border-emerald-400/50 hover:bg-emerald-400/[0.04]",
  amber: "border-amber-400/20 text-amber-300 hover:border-amber-400/50 hover:bg-amber-400/[0.04]",
};

const MODULE_GROUPS = ["All", "AI", "Opposition", "Fundraising", "Advertising", "Agency", "Volunteers", "Ops"];

function AtAGlanceSection() {
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("All");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return MODULE_CATALOG.filter(m => {
      if (group !== "All" && m.g !== group) return false;
      if (!needle) return true;
      return m.t.toLowerCase().includes(needle) || m.k.toLowerCase().includes(needle);
    });
  }, [q, group]);

  const highlight = (text) => {
    const needle = q.trim();
    if (!needle) return text;
    const idx = text.toLowerCase().indexOf(needle.toLowerCase());
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-cyan-400/30 text-white rounded px-0.5">
          {text.slice(idx, idx + needle.length)}
        </mark>
        {text.slice(idx + needle.length)}
      </>
    );
  };

  return (
    <section className="max-w-7xl mx-auto px-6 py-14 border-y border-white/5" data-testid="at-a-glance-section">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-1">Everything In One Login</div>
          <h2 className="font-display text-lg font-bold">{MODULE_CATALOG.length} Modules. One Political Operating System.</h2>
        </div>
        <div className="text-xs text-slate-500 max-w-md">Search or filter to zero in on what your race needs. Every card below is a first-class feature — not an integration.</div>
      </div>

      {/* Search + filter chips */}
      <div className="flex items-center gap-3 flex-wrap mb-5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Search modules — try "SMS", "PAC", "opposition", "brief"…'
            data-testid="feature-search-input"
            className="w-full bg-slate-900/60 border border-white/10 rounded-full pl-10 pr-9 py-2 text-sm text-white focus:border-cyan-400/50 focus:outline-none placeholder-slate-600"
          />
          {q && (
            <button onClick={() => setQ("")} data-testid="feature-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="feature-search-groups">
          {MODULE_GROUPS.map(g => (
            <button key={g} onClick={() => setGroup(g)}
              data-testid={`feature-group-${g.toLowerCase()}`}
              className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${group === g ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400 hover:border-cyan-400/30 hover:text-white"}`}>
              {g}
            </button>
          ))}
        </div>
        <div className="text-[10px] text-slate-500 ml-auto" data-testid="feature-search-count">
          {filtered.length} of {MODULE_CATALOG.length} modules
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500" data-testid="feature-search-empty">
          No match for &quot;{q}&quot;. Try a broader keyword — <button onClick={() => setQ("")} className="text-cyan-300 hover:underline">show all</button>.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="at-a-glance-grid">
          {filtered.map((m) => (
            <div key={m.t} data-testid={`module-card-${m.t}`}
                 className={`rounded-lg border ${TILE_COLORS[m.c]} bg-white/[0.01] p-3 transition-colors flex flex-col items-start gap-2`}>
              <m.icon className="w-4 h-4" />
              <div className="text-xs font-medium text-slate-200 leading-tight">{highlight(m.t)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  // Hero headline A/B — variant is stable per anon visitor; conversion is
  // measured by any of the two hero CTAs firing an experiment_click.
  const heroVariant = useExperiment("hero_headline_copy", ["ads_and_fundraising_os", "winning_races_os"]);
  const heroHeadline =
    heroVariant === "winning_races_os"
      ? { eyebrow: "The Political Operating System", h1a: "The Operating System for", h1b: "Winning Races." }
      : { eyebrow: "TotalReach Political Intelligence™", h1a: "Political Advertising", h1b: "& Fundraising OS." };
  const heroSignup = () => { trackExperimentClick("hero_headline_copy", heroVariant, { action: "signup" }); navigate("/signup"); };
  const heroDemo = () => { trackExperimentClick("hero_headline_copy", heroVariant, { action: "book_demo" }); navigate("/request-demo"); };
  return (
    <div className="min-h-screen bg-app text-white" data-testid="landing-page">
      <Seo title="TotalReach — The Political Campaign Operating System" path="/" jsonLd={[orgJsonLd(), softwareJsonLd()]} />
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgb(var(--ok-400)_/_0.12),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgb(var(--brand-400)_/_0.10),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-4 px-3 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/5" data-testid="hero-eyebrow">
            <Sparkles className="w-3.5 h-3.5" /> {heroHeadline.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-3xl" data-testid="hero-headline">
            {heroHeadline.h1a} <span className="brand-gradient">{heroHeadline.h1b}</span>
          </h1>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-2xl">
            TotalReach is the political operating system that connects polling intelligence, public issue tracking and geography
            with AI strategy, CTV-first media execution, fundraising and honest before-vs-after measurement — so your campaign
            runs on evidence, not guesswork. One login. One report. Built for the election cycle.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={heroDemo} data-testid="hero-book-demo-btn"
              className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-7 py-3 hover:opacity-90 transition-opacity inline-flex items-center gap-2">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </button>
            <DemoVideo poster />
            <button onClick={heroSignup} data-testid="hero-signup-btn"
              className="border border-white/20 text-white rounded-full px-7 py-3 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
              Ready to start? Sign up
            </button>
          </div>
          <button onClick={() => navigate("/login")} data-testid="hero-login-link"
            className="mt-4 text-sm text-slate-400 hover:text-cyan-300 transition-colors underline underline-offset-4 decoration-slate-600 hover:decoration-cyan-400">
            Returning? Log in
          </button>
          <div className="mt-14 flex flex-wrap gap-2">
            {CHANNELS.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-slate-300">
                <c.icon className="w-3.5 h-3.5 text-cyan-300" /> {c.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* At-a-glance capability grid — dense marketing overview */}
      <AtAGlanceSection />

      {/* Political Operating System loop */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="political-os-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">The Political Operating System</div>
        <h2 className="font-display text-lg font-bold">From Intelligence to Impact — One Connected Loop</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Most platforms only buy media. TotalReach starts earlier — with the race itself — and ends with measurement you can defend.</p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          {OS_LOOP.map((s) => (
            <div key={s.n} className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid={`os-step-${s.n}`}>
              <div className="font-display text-2xl font-bold brand-gradient">{s.n}</div>
              <div className="font-semibold text-sm mt-2">{s.t}</div>
              <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="intel-features-grid">
          {INTEL_FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-cyan-400/20 bg-gradient-to-b from-cyan-400/[0.05] to-transparent p-6 hover:border-cyan-400/40 transition-colors">
              <f.icon className="w-5 h-5 text-cyan-300" />
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI in every step */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="ai-features-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">AI In Every Step</div>
        <h2 className="font-display text-lg font-bold">Your Campaign Staff Just Got an AI Department</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">From the first plan to the final push, TotalReach&apos;s AI does the heavy lifting — you stay in command with one-click approvals.</p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AI_FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-cyan-400/20 bg-gradient-to-b from-cyan-400/[0.05] to-transparent p-6 hover:border-cyan-400/40 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
                <f.icon className="w-4.5 h-4.5 text-cyan-300" />
              </div>
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-xl border border-fuchsia-400/25 bg-fuchsia-400/[0.05] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="no-creatives-callout">
          <Wand2 className="w-6 h-6 text-fuchsia-300 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-semibold">Don&apos;t have creatives? You don&apos;t need any.</div>
            <p className="text-sm text-slate-400 mt-0.5">Tell the AI about your race and it drafts display ads, TV scripts, radio spots, AI-presenter video and voiceover — on-brand, on-message and compliant, ready the same day.</p>
          </div>
          <button onClick={() => navigate("/request-demo")} className="shrink-0 border border-fuchsia-400/40 text-fuchsia-200 rounded-full px-5 py-2 text-sm hover:bg-fuchsia-400/10 transition-colors">See it in a demo</button>
        </div>
      </section>

      {/* Political-first */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="political-first-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Political-First, Not Political-Also</div>
        <h2 className="font-display text-lg font-bold">Purpose-Built for Campaigns, Committees, PACs & Public Affairs</h2>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {POLITICAL_FIRST.map((f) => (
            <div key={f.t} className="rounded-xl border border-white/10 bg-white/[0.02] p-6 hover:border-cyan-400/30 transition-colors">
              <f.icon className="w-5 h-5 text-emerald-300" />
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Opposition Intelligence — Meta + Google + PAC alerts */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="opposition-intel-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Opposition Intelligence</div>
        <h2 className="font-display text-lg font-bold">See Every Dollar Your Opposition Spends — Before It Moves a Poll</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-3xl">Three ad-transparency sources — Meta, Google and AdLibrary.com — feed one unified opposition dashboard. FEC Schedule E monitoring turns unusual PAC spend into an urgent morning notification.</p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="opposition-intel-grid">
          {OPPOSITION_INTEL.map((f) => (
            <div key={f.t} className="rounded-xl border border-fuchsia-400/25 bg-gradient-to-b from-fuchsia-400/[0.04] to-transparent p-6 hover:border-fuchsia-400/50 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-fuchsia-400/10 border border-fuchsia-400/25 flex items-center justify-center">
                <f.icon className="w-4.5 h-4.5 text-fuchsia-300" />
              </div>
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fundraising in one platform */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="fundraising-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Fundraising, Connected</div>
        <h2 className="font-display text-lg font-bold">Donations, Text-to-Give and Donor CRM — In the Same Login</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-3xl">Every dollar attributed back to the ad, microsite, QR code or SMS keyword that raised it. No third-party donor CRM to bolt on.</p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FUNDRAISING_FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-emerald-400/25 bg-gradient-to-b from-emerald-400/[0.04] to-transparent p-6 hover:border-emerald-400/50 transition-colors">
              <f.icon className="w-5 h-5 text-emerald-300" />
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Agency Portfolio */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="agency-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Built for Agencies & Consultants</div>
        <h2 className="font-display text-lg font-bold">Run 12 Campaigns From One Login — Without 12 Passwords</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-3xl">Whether you&apos;re a solo consultant with three clients or a big-shop media agency with a full slate, TotalReach scales without friction.</p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AGENCY_FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-white/10 bg-white/[0.02] p-6 hover:border-cyan-400/30 transition-colors">
              <f.icon className="w-5 h-5 text-cyan-300" />
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Volunteer + Field Ops */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="volunteer-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Volunteers & Field Operations</div>
        <h2 className="font-display text-lg font-bold">Turn Every Supporter Into a Recurring Contributor</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-3xl">Shift signups, event RSVPs, QR-code attribution, opposition ad sightings — all rolled into a gamified leaderboard that keeps your base engaged from filing week to election night.</p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {VOLUNTEER_FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-amber-400/25 bg-gradient-to-b from-amber-400/[0.04] to-transparent p-6 hover:border-amber-400/50 transition-colors">
              <f.icon className="w-5 h-5 text-amber-300" />
              <div className="font-display font-semibold mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 60-second Claim Onboarding */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="onboarding-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Zero-to-Live in an Afternoon</div>
        <h2 className="font-display text-lg font-bold">Claim Your Race → Fully Set Up in 8 Guided Steps</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-3xl">No implementation team, no month-long onboarding. An auto-progressing checklist walks every new candidate from FEC verification to first ad — with the AI Chief of Staff standing by to explain each step.</p>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {CLAIM_ONBOARDING.map((f) => (
            <div key={f.t} className="rounded-xl border border-cyan-400/25 bg-gradient-to-b from-cyan-400/[0.05] to-transparent p-6">
              <f.icon className="w-6 h-6 text-cyan-300" />
              <div className="font-display font-semibold text-base mt-3">{f.t}</div>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Premium environments */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Premium Environments</div>
        <h2 className="font-display text-lg font-bold">Not Just More Impressions — Better Ones</h2>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Brand-safe, premium inventory across every screen a voter touches.</p>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {ENVIRONMENTS.map((e) => (
            <div key={e.title} className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
              <div className="font-display font-semibold">{e.title}</div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {e.items.map((i) => {
                  const Icon = i.icon;
                  return (
                    <span key={i.name} data-testid={`env-pill-${i.name}`}
                      className={`inline-flex items-center gap-1.5 text-[11px] py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 ${i.domain || Icon ? "pl-1.5 pr-2.5" : "px-2.5"}`}>
                      {i.domain && (
                        <img src={`https://www.google.com/s2/favicons?domain=${i.domain}&sz=64`} alt=""
                          loading="lazy" data-testid="env-brand-logo"
                          className="w-4 h-4 rounded-[3px] bg-paper p-px object-contain"
                          onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                      )}
                      {Icon && <Icon className="w-3.5 h-3.5 text-cyan-300/80" />}
                      {i.name}
                    </span>
                  );
                })}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-600">{e.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiation — 3-way compare */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="differentiation-section">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Why Not Just Buy Ads Directly?</div>
        <h2 className="font-display text-lg font-bold">The Difference Between Buying Ads and Running a Campaign</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {DIFF_COLUMNS.map((col) => (
            <div key={col.title} className={`rounded-xl border p-6 ${
              col.tone === "good" ? "border-cyan-400/40 bg-cyan-400/[0.06]" :
              col.tone === "mid" ? "border-white/10 bg-white/[0.02]" : "border-white/10 bg-white/[0.02]"}`}>
              <div className={`font-display font-semibold ${col.tone === "good" ? "text-cyan-300" : "text-slate-300"}`}>{col.title}</div>
              {col.items.map((i) => (
                <div key={i} className={`mt-3 flex items-start gap-2 text-sm ${col.tone === "good" ? "text-slate-200" : "text-slate-400"}`}>
                  {col.tone === "good"
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                    : <XCircle className={`w-4 h-4 shrink-0 mt-0.5 ${col.tone === "mid" ? "text-amber-400/70" : "text-rose-400/70"}`} />}
                  {i}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Voter journey */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Voter Journey</div>
        <h2 className="font-display text-lg font-bold">The Voter Journey, Connected</h2>
        <p className="text-slate-400 text-sm mt-2">TotalReach covers every stage — from first impression through persuasion to turnout and grassroots dollars.</p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          {JOURNEY.map((j) => (
            <div key={j.n} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <div className="font-display text-2xl font-bold brand-gradient">{j.n}</div>
              <div className="font-semibold text-sm mt-2">{j.t}</div>
              <div className="text-xs text-slate-500 mt-1">{j.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Measurement / stats band */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-cyan-400/[0.06] to-blue-500/[0.05] p-8">
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 font-medium mb-2">Measurement Beyond Clicks</div>
          <p className="text-sm text-slate-400 max-w-2xl mb-6">Households reached, completion rates, frequency and pacing across every channel — plus AI that flags what to fix before it costs you votes.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.l}>
                <div className="font-display text-3xl font-bold brand-gradient">{s.v}</div>
                <div className="text-sm text-slate-300 mt-1">{s.l}</div>
                <div className="text-xs text-emerald-300 mt-0.5">{s.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Ready to Reach More Voters With More Precision?</h2>
        <p className="mt-3 text-slate-400 max-w-xl mx-auto">See the AI build a full campaign — plan, audience, creative and fundraiser — live in your demo.</p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <button onClick={() => navigate("/request-demo")} data-testid="cta-book-demo-btn"
            className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-8 py-3 hover:opacity-90 transition-opacity inline-flex items-center gap-2">
            <Target className="w-4 h-4" /> Book a Demo
          </button>
          <button onClick={() => navigate("/signup")} data-testid="cta-signup-btn"
            className="border border-white/20 text-white rounded-full px-8 py-3 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
            Create your account
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <TotalReachLogo size={22} />
          <div className="flex items-center gap-5">
            <button onClick={() => navigate("/pricing")} data-testid="footer-pricing-link" className="hover:text-cyan-300">Pricing</button>
            <button onClick={() => navigate("/whats-new")} data-testid="footer-whats-new-link" className="hover:text-cyan-300">What&apos;s New</button>
            <a href="mailto:info@totalreach.us" className="hover:text-cyan-300">info@totalreach.us</a>
            <a href="tel:+14699911289" className="hover:text-cyan-300">+1 (469) 991-1289</a>
          </div>
          <div>© 2026 TotalReach.us — AI-enabled, compliance-first political advertising</div>
        </div>
      </footer>

      <StickyBookDemo placement="landing" />
    </div>
  );
}
